import { useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { getScopedTenantId, TENANT_SCOPE_REQUIRED_MESSAGE } from 'services/api/config/scopedTenant';
import { UserRole, hasAnyRole } from 'types/userRoles';

export interface UseTenantScopeResult {
    /** True when the current user is a super-admin. */
    isSuperAdmin: boolean;
    /** Scoped tenant UUID from session storage, or null if none selected. */
    scopedTenantId: string | null;
    /**
     * True when a super-admin must select a tenant before scoped actions/API calls.
     * Non–super-admins are never blocked by tenant scope.
     */
    isTenantScopeRequired: boolean;
    /** Tooltip copy shown when tenant scope is required. */
    requiredMessage: string;
}

export const useTenantScope = (): UseTenantScopeResult => {
    const { userRoles } = useAuth();
    const isSuperAdmin = hasAnyRole(userRoles, [UserRole.SUPER_ADMIN]);
    const scopedTenantId = getScopedTenantId();

    return useMemo(
        () => ({
            isSuperAdmin,
            scopedTenantId,
            isTenantScopeRequired: isSuperAdmin && !scopedTenantId,
            requiredMessage: TENANT_SCOPE_REQUIRED_MESSAGE,
        }),
        [isSuperAdmin, scopedTenantId]
    );
};
